// Hoàn tác (VTask #531, gói A, nâng cấp 9) — chỉ cho thao tác ĐẢO NGƯỢC ĐƯỢC: nhãn + đóng/mở (GitHub), người phụ trách,
// người duyệt (D1). Logic ở đây là hàm thuần (test: tests/hoan-tac.test.ts); phần gọi mạng nằm ở useHanhDong (du-lieu.ts),
// ghi lại bằng CHÍNH các lệnh ghi sẵn có (PATCH issue, PUT phu-trach, PUT nguoi-duyet) — không có đường ghi mới.
//
// Ngưỡng an toàn:
//   · Mỗi thao tác ghi ẢNH TRƯỚC (lúc bấm) và ẢNH SAU (thứ thao tác vừa ghi). Hoàn tác đọc dữ liệu MỚI NHẤT; trục nào
//     sắp ghi đè mà hiện tại khác ảnh sau (người khác sửa, đồng bộ Project đổi nhãn…) thì KHÔNG ghi việc đó.
//   · Chỉ trục thao tác thật sự đã ghi mới có trong ảnh (trục vắng mặt = không kiểm, không ghi).
//   · Hết hạn (8 giây) thì thôi; một thao tác chỉ hoàn tác được một lần.

export const HAN_HOAN_TAC_MS = 8000;

// Trục GitHub (nhan + mo) luôn đi cùng nhau: ghi đè nhãn khi việc đã bị đóng/mở ở nơi khác cũng là ghi đè.
export type AnhViec = { nhan?: string[]; mo?: boolean; nguoi?: number[]; nguoi_duyet?: number | null };
export type MucHoanTac = { so: number; truoc: AnhViec; sau: AnhViec };

// Lệnh ghi để đưa một việc về ảnh trước. Trường vắng = không ghi trục đó.
export type LenhHoanTac = {
  issue?: { labels?: string[]; state?: 'open' | 'closed'; state_reason?: 'completed' };
  nguoi?: number[];
  nguoi_duyet?: number | null;
};

const cungTap = (a: readonly (string | number)[], b: readonly (string | number)[]) => {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return s.size === new Set(b).size && b.every((x) => s.has(x));
};

const coGh = (a: AnhViec) => a.nhan !== undefined && a.mo !== undefined;

// Mục hoàn tác cho một việc. Trả null khi thao tác không đổi gì (khỏi hiện nút cho một cú bấm vô nghĩa).
export function taoMuc(so: number, truoc: AnhViec, sau: AnhViec): MucHoanTac | null {
  const t: AnhViec = {}, s: AnhViec = {};
  if (coGh(truoc) && coGh(sau) && (!cungTap(truoc.nhan!, sau.nhan!) || truoc.mo !== sau.mo)) {
    t.nhan = [...truoc.nhan!]; t.mo = truoc.mo; s.nhan = [...sau.nhan!]; s.mo = sau.mo;
  }
  if (truoc.nguoi !== undefined && sau.nguoi !== undefined && !cungTap(truoc.nguoi, sau.nguoi)) {
    t.nguoi = [...truoc.nguoi]; s.nguoi = [...sau.nguoi];
  }
  if (truoc.nguoi_duyet !== undefined && sau.nguoi_duyet !== undefined && truoc.nguoi_duyet !== sau.nguoi_duyet) {
    t.nguoi_duyet = truoc.nguoi_duyet; s.nguoi_duyet = sau.nguoi_duyet;
  }
  return Object.keys(s).length ? { so, truoc: t, sau: s } : null;
}

// Các trục cần đọc lại trước khi hoàn tác.
export const trucCanDoc = (m: MucHoanTac) => ({ gh: coGh(m.sau), nguoi: m.sau.nguoi !== undefined, nguoi_duyet: m.sau.nguoi_duyet !== undefined });

// Hiện tại còn đúng ảnh sau không — so MỌI trục sắp ghi. Thiếu dữ liệu hiện tại cho một trục = coi là đã đổi (không đoán).
export function conNguyenSauThaoTac(m: MucHoanTac, hien_tai: AnhViec): boolean {
  const s = m.sau;
  if (coGh(s)) {
    if (!coGh(hien_tai)) return false;
    if (hien_tai.mo !== s.mo || !cungTap(hien_tai.nhan!, s.nhan!)) return false;
  }
  if (s.nguoi !== undefined && (hien_tai.nguoi === undefined || !cungTap(hien_tai.nguoi, s.nguoi))) return false;
  if (s.nguoi_duyet !== undefined && (hien_tai.nguoi_duyet === undefined || hien_tai.nguoi_duyet !== s.nguoi_duyet)) return false;
  return true;
}

export function lenhHoanTac(m: MucHoanTac): LenhHoanTac {
  const { truoc: t, sau: s } = m;
  const l: LenhHoanTac = {};
  if (coGh(s) && coGh(t)) {
    const issue: NonNullable<LenhHoanTac['issue']> = {};
    if (!cungTap(t.nhan!, s.nhan!)) issue.labels = [...t.nhan!];
    if (t.mo !== s.mo) {
      issue.state = t.mo ? 'open' : 'closed';
      if (!t.mo) issue.state_reason = 'completed';
    }
    if (Object.keys(issue).length) l.issue = issue;
  }
  if (t.nguoi !== undefined && s.nguoi !== undefined) l.nguoi = [...t.nguoi];
  if (t.nguoi_duyet !== undefined && s.nguoi_duyet !== undefined) l.nguoi_duyet = t.nguoi_duyet;
  return l;
}

export type KetQuaHoanTac = { xong: number[]; da_doi: number[]; loi: { so: number; loi: string }[] };

// Chạy hoàn tác cả lô. Từng việc độc lập: việc này đã đổi / lỗi thì vẫn làm tiếp việc khác, cuối cùng báo việc nào CHƯA hoàn
// tác. Đi ngược thứ tự thao tác. `doc` phải trả dữ liệu MỚI NHẤT (không lấy từ bộ nhớ đệm của giao diện).
export async function chayHoanTac(
  ds: MucHoanTac[],
  io: { doc: (m: MucHoanTac) => Promise<AnhViec>; ghi: (so: number, lenh: LenhHoanTac) => Promise<void> },
): Promise<KetQuaHoanTac> {
  const kq: KetQuaHoanTac = { xong: [], da_doi: [], loi: [] };
  for (const m of [...ds].reverse()) {
    try {
      const hien_tai = await io.doc(m);
      if (!conNguyenSauThaoTac(m, hien_tai)) { kq.da_doi.push(m.so); continue; }
      await io.ghi(m.so, lenhHoanTac(m));
      kq.xong.push(m.so);
    } catch (e) {
      kq.loi.push({ so: m.so, loi: e instanceof Error ? e.message : String(e) });
    }
  }
  return kq;
}

const dsSo = (ds: number[]) => ds.map((s) => '#' + s).join(', ');

export function cauKetQua(kq: KetQuaHoanTac, mo_ta: string): { noi_dung: string; kieu: 'thuong' | 'loi' } {
  const tong = kq.xong.length + kq.da_doi.length + kq.loi.length;
  if (tong === 1) {
    if (kq.xong.length) return { noi_dung: `Đã hoàn tác: ${mo_ta}`, kieu: 'thuong' };
    if (kq.da_doi.length) return { noi_dung: `Việc đã thay đổi, không hoàn tác được (#${kq.da_doi[0]}).`, kieu: 'loi' };
    return { noi_dung: `Chưa hoàn tác được #${kq.loi[0].so}: ${kq.loi[0].loi}`, kieu: 'loi' };
  }
  const phan = [`Đã hoàn tác ${kq.xong.length}/${tong} việc.`];
  if (kq.da_doi.length) phan.push(`Việc đã thay đổi, không hoàn tác được: ${dsSo([...kq.da_doi].reverse())}.`);
  if (kq.loi.length) phan.push(`Lỗi, chưa hoàn tác: ${dsSo(kq.loi.map((l) => l.so).reverse())}.`);
  return { noi_dung: phan.join(' '), kieu: kq.da_doi.length || kq.loi.length ? 'loi' : 'thuong' };
}

/* ── Ngăn xếp thao tác còn hạn (ngoài React; nút trên toast và Ctrl+Z dùng chung) ── */

export type ThaoTac = { id: number; mo_ta: string; het_han: number; chay: () => Promise<void> };

export const TOI_DA_NGAN_XEP = 20;

export function themThaoTac(ds: ThaoTac[], tt: ThaoTac, bay_gio: number): ThaoTac[] {
  return [...ds.filter((x) => x.het_han > bay_gio), tt].slice(-TOI_DA_NGAN_XEP);
}

// Thao tác gần nhất còn hạn (null = không còn gì).
export function ganNhatConHan(ds: ThaoTac[], bay_gio: number): ThaoTac | null {
  for (let i = ds.length - 1; i >= 0; i--) if (ds[i].het_han > bay_gio) return ds[i];
  return null;
}

let ngan_xep: ThaoTac[] = [];
let dem = 0;

export function dangKyThaoTac(mo_ta: string, chay: () => Promise<void>, bay_gio = Date.now()): ThaoTac {
  const tt: ThaoTac = { id: ++dem, mo_ta, het_han: bay_gio + HAN_HOAN_TAC_MS, chay };
  ngan_xep = themThaoTac(ngan_xep, tt, bay_gio);
  return tt;
}

// Lấy RA (một lần duy nhất) thao tác theo id; null nếu đã chạy / hết hạn / không có.
export function layRa(id: number, bay_gio = Date.now()): ThaoTac | null {
  const tt = ngan_xep.find((x) => x.id === id);
  ngan_xep = ngan_xep.filter((x) => x.id !== id && x.het_han > bay_gio);
  return tt && tt.het_han > bay_gio ? tt : null;
}

export function layRaGanNhat(bay_gio = Date.now()): ThaoTac | null {
  const tt = ganNhatConHan(ngan_xep, bay_gio);
  return tt ? layRa(tt.id, bay_gio) : null;
}

// Xem (KHÔNG lấy ra) thao tác gần nhất còn hạn — bảng lệnh dùng để biết có hiện lệnh "Hoàn tác" không.
export const xemGanNhat = (bay_gio = Date.now()): ThaoTac | null => ganNhatConHan(ngan_xep, bay_gio);

export function xoaNganXep() { ngan_xep = []; }
